import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { SEV_PRICING, formatCurrencyDE, getDiscount } from '../config/sevPricing';

const SondereigentumsverwaltungPage = () => {
  const navigate = useNavigate();
  const [units, setUnits] = useState(1);
  const [packageKey, setPackageKey] = useState('komfort');
  const [netColdRent, setNetColdRent] = useState('');
  const [parkingCount, setParkingCount] = useState(0);
  const [wantsNewRental, setWantsNewRental] = useState(false);
  const [includeOnboarding, setIncludeOnboarding] = useState(false);

  const selectedPackage = SEV_PRICING.packages[packageKey];

  const calculation = useMemo(() => {
    const qty = Math.max(1, Number(units) || 1);
    const basePricePerUnit = selectedPackage.monthlyNetPerUnit;
    const packageSum = basePricePerUnit * qty;
    const discount = getDiscount(qty);

    if (discount.type === 'request') {
      return {
        isRequest: true,
        discount,
        packageSum,
      };
    }

    const discountedPackageSum = packageSum * (1 - (discount.value / 100));
    const parkingNet = (Number(parkingCount) || 0) * SEV_PRICING.parkingNetPerMonth;
    const monthlyNet = discountedPackageSum + parkingNet;
    const monthlyGross = monthlyNet * (1 + SEV_PRICING.vatRate);
    const yearlyNet = monthlyNet * 12;
    const yearlyGross = monthlyGross * 12;

    const oneTimeNewRentalNet = wantsNewRental ? (Number(netColdRent) || 0) : 0;
    const oneTimeOnboardingNet = includeOnboarding ? qty * SEV_PRICING.onboardingNetPerUnit : 0;
    const oneTimeTotalNet = oneTimeNewRentalNet + oneTimeOnboardingNet;
    const oneTimeTotalGross = oneTimeTotalNet * (1 + SEV_PRICING.vatRate);

    return {
      isRequest: false,
      discount,
      packageSum,
      discountedPackageSum,
      parkingNet,
      monthlyNet,
      monthlyGross,
      yearlyNet,
      yearlyGross,
      oneTimeNewRentalNet,
      oneTimeOnboardingNet,
      oneTimeTotalNet,
      oneTimeTotalGross,
    };
  }, [units, parkingCount, wantsNewRental, includeOnboarding, netColdRent, selectedPackage.monthlyNetPerUnit]);

  const goToContact = (source = 'sev') => {
    const params = new URLSearchParams({
      source,
      leistung: 'SEV',
      paket: selectedPackage.name,
      einheiten: String(Math.max(1, Number(units) || 1)),
      stellplaetze: String(Math.max(0, Number(parkingCount) || 0)),
      neuvermietung: wantsNewRental ? 'ja' : 'nein',
      onboarding: includeOnboarding ? 'ja' : 'nein',
    });

    if (!calculation.isRequest) {
      params.set('monat_netto', String(calculation.monthlyNet.toFixed(2)));
      params.set('monat_brutto', String(calculation.monthlyGross.toFixed(2)));
      params.set('jahr_netto', String(calculation.yearlyNet.toFixed(2)));
      params.set('jahr_brutto', String(calculation.yearlyGross.toFixed(2)));
      params.set('einmalkosten_netto', String(calculation.oneTimeTotalNet.toFixed(2)));
      params.set('einmalkosten_brutto', String(calculation.oneTimeTotalGross.toFixed(2)));
    }

    navigate(`/kontakt?${params.toString()}`);
  };

  return (
    <Box sx={{ bgcolor: '#f5f7fb', minHeight: '100vh', py: { xs: 4, md: 7 } }}>
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Box sx={{ textAlign: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
              <Button
                variant="text"
                onClick={() => navigate('/')}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Zur Hauptseite
              </Button>
            </Box>
            <Typography variant="overline" sx={{ color: '#0d9488', letterSpacing: 1.2, fontWeight: 700 }}>
              LEISTUNG
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', mb: 1 }}>
              Sondereigentumsverwaltung (SEV)
            </Typography>
            <Typography variant="h6" sx={{ color: '#475569', fontWeight: 400 }}>
              Weniger Aufwand, mehr Kontrolle: Wir uebernehmen Mietverwaltung, Mahnwesen, Instandhaltung und Reporting mit festen Ansprechpartnern, transparenten Monatsreports und rechtssicherer Prozessfuehrung.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {Object.values(SEV_PRICING.packages).map((pkg) => (
              <Grid item xs={12} md={6} key={pkg.key}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    border: pkg.recommended ? '2px solid #0d9488' : '1px solid #dbe3ee',
                    boxShadow: pkg.recommended ? '0 16px 40px rgba(13,148,136,0.12)' : '0 8px 24px rgba(15,23,42,0.06)',
                  }}
                >
                  <CardContent>
                    <Stack spacing={1.5}>
                      {pkg.recommended && <Chip label="Empfohlen" color="success" sx={{ width: 'fit-content' }} />}
                      <Typography variant="h5" sx={{ fontWeight: 800 }}>{pkg.name}</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a' }}>
                        {formatCurrencyDE(pkg.monthlyNetPerUnit)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748b' }}>netto je Einheit / Monat, zzgl. 19 % USt</Typography>
                      <Divider />
                      {pkg.features.map((item) => (
                        <Typography key={item} variant="body2" sx={{ color: '#334155' }}>- {item}</Typography>
                      ))}
                      <Box sx={{ pt: 1 }}>
                        <Button variant={pkg.recommended ? 'contained' : 'outlined'} onClick={() => {
                          setPackageKey(pkg.key);
                          goToContact(pkg.key === 'basis' ? 'mandatsuebernahme' : 'paketkarte');
                        }} sx={{ textTransform: 'none', fontWeight: 700 }}>
                          {pkg.key === 'basis' ? 'Mandatsuebernahme anfragen' : 'Angebot anfordern'}
                        </Button>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Card sx={{ borderRadius: 3, border: '1px solid #dbe3ee' }}>
            <CardContent>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>SEV-Kalkulator</Typography>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Anzahl Einheiten"
                    value={units}
                    onChange={(e) => setUnits(e.target.value)}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>Paket</Typography>
                    <RadioGroup row value={packageKey} onChange={(e) => setPackageKey(e.target.value)}>
                      <FormControlLabel value="basis" control={<Radio />} label="Basis" />
                      <FormControlLabel value="komfort" control={<Radio />} label="Komfort" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Stellplaetze/Garagen"
                    value={parkingCount}
                    onChange={(e) => setParkingCount(e.target.value)}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Nettokaltmiete pro Monat (optional)"
                    value={netColdRent}
                    onChange={(e) => setNetColdRent(e.target.value)}
                    inputProps={{ min: 0, step: '0.01' }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <FormControlLabel
                      control={<Switch checked={wantsNewRental} onChange={(e) => setWantsNewRental(e.target.checked)} />}
                      label="Neuvermietung gewuenscht"
                    />
                    <FormControlLabel
                      control={<Switch checked={includeOnboarding} onChange={(e) => setIncludeOnboarding(e.target.checked)} />}
                      label="Onboarding einrechnen"
                    />
                  </Stack>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {calculation.isRequest ? (
                <Alert severity="info">
                  Ab 25 Einheiten erstellen wir ein individuelles Angebot. Bitte senden Sie eine Anfrage.
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Laufendes Honorar</Typography>
                        <Typography variant="body2">Monat netto: {formatCurrencyDE(calculation.monthlyNet)}</Typography>
                        <Typography variant="body2">Monat brutto: {formatCurrencyDE(calculation.monthlyGross)}</Typography>
                        <Typography variant="body2">Jahr netto: {formatCurrencyDE(calculation.yearlyNet)}</Typography>
                        <Typography variant="body2">Jahr brutto: {formatCurrencyDE(calculation.yearlyGross)}</Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#64748b' }}>
                          Mengenrabatt: {calculation.discount.label}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Einmalkosten</Typography>
                        <Typography variant="body2">Neuvermietung netto: {formatCurrencyDE(calculation.oneTimeNewRentalNet)}</Typography>
                        <Typography variant="body2">Onboarding netto: {formatCurrencyDE(calculation.oneTimeOnboardingNet)}</Typography>
                        <Typography variant="body2">Einmalkosten netto: {formatCurrencyDE(calculation.oneTimeTotalNet)}</Typography>
                        <Typography variant="body2">Einmalkosten brutto: {formatCurrencyDE(calculation.oneTimeTotalGross)}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              <Box sx={{ mt: 3 }}>
                <Button variant="contained" size="large" sx={{ textTransform: 'none', fontWeight: 700 }} onClick={() => goToContact('kalkulator')}>
                  Dieses Angebot anfordern
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 3, border: '1px solid #dbe3ee' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5 }}>Sonderleistungen (separat)</Typography>
              <Typography variant="body2" sx={{ color: '#334155' }}>Neuvermietung: {SEV_PRICING.referralHints.neuvermietung}</Typography>
              <Typography variant="body2" sx={{ color: '#334155' }}>Onboarding / Mandatseinrichtung: {formatCurrencyDE(SEV_PRICING.onboardingNetPerUnit)} je Einheit</Typography>
              <Typography variant="body2" sx={{ color: '#334155' }}>Modernisierungs-/Sanierungsbegleitung: {SEV_PRICING.referralHints.modernisierung}</Typography>
              <Typography variant="body2" sx={{ color: '#334155' }}>Sonderaufwand/Rechts- und Konfliktfaelle: {SEV_PRICING.referralHints.sonderaufwand}</Typography>
              <Typography variant="body2" sx={{ color: '#334155' }}>Wohnungsabnahme/-uebergabe: {SEV_PRICING.referralHints.uebergabe}</Typography>
            </CardContent>
          </Card>

          <Alert severity="info">
            Alle Preise netto zzgl. 19 % USt. Das Verwalterhonorar ist nicht auf Mieter umlagefaehig. Beim vermietenden Eigentuemer in der Regel als Werbungskosten absetzbar (keine Steuerberatung). Es gibt keine gesetzliche Gebuehrenordnung; die Konditionen sind frei vereinbar. Verbindliche Konditionen ergeben sich ausschliesslich aus dem individuellen SEV-Vertrag.
          </Alert>
        </Stack>
      </Container>
    </Box>
  );
};

export default SondereigentumsverwaltungPage;
